import {
	ContactFilterCriteriaProperty,
	IAccountTag,
	IBulkContactsRequest,
	IContactsFilterRequest,
	IImpersonationContext,
	ISortDescriptor,
} from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { DefaultContactsFilterRequest } from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { searchRequestToQueryStringParams } from '../../../../../models/UiUtils';
import { usePutTagCategoryMutation } from '../../../../../queries/Tag/usePutTagCategoryMutation';
import { useTagColorMutation } from '../../../../../queries/Tag/useTagColorMutation';
import { useTagLockedMutation } from '../../../../../queries/Tag/useTagLockedMutation';
import { invalidateTagCategoryQuery } from '../../../../../queries/TagCategory/useGetTagCategoryQuery';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Column } from '../../../../components-v2/PageLayout';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { ThemeColors } from '../../../../components-v2/Theme/theme';
import { Button, ButtonBase } from '../../../../components/Button';
import { DataGridRow, DataGridRowItem } from '../../../../components/DataGrid';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { EllipsisIcon } from '../../../../components/svgs/icons/EllipsisIcon';
import { PlusIcon } from '../../../../components/svgs/icons/PlusIcon';
import { ROUTES } from '../../../../constants/routes';
import { brandSecondary } from '../../../../styles/colors';
import { ColorPicker } from '../ColorPicker/ColorPicker';
import { TagAlert } from '../TagAlert';
import { useTagsDataContext } from '../TagsContexts/TagsDataContext';
import { TagFavoriteColumn } from '../TagsFavorites/TagFavoriteColumn';
import { styleSheet } from './styles';
import { TagLockToggle } from './TagLockToggle';

interface TagRowProps {
	tag: IAccountTag;
	isSelected?: boolean;
	onToggleTagRow: (id: string) => void;
	isAdminContext: boolean;
	refreshTags: () => void;
	onEditTagAlert: (tag: IAccountTag) => void;
	onUpdateCategory: (tag: IAccountTag) => void;
	impersonationContext: IImpersonationContext;
}

export const TagRow: FC<TagRowProps> = ({
	tag,
	isSelected,
	onToggleTagRow,
	isAdminContext,
	impersonationContext,
	refreshTags,
	onEditTagAlert,
	onUpdateCategory,
}) => {
	const history = useHistory();
	const errors = useErrorMessages();
	const userSession = useUserSession();
	// Lock toggle is admin-only — the API rejects PUT /tag/{id}/isLocked for non-admins.
	// Match the same gate used elsewhere in the tag-management UI.
	const canManageTagLock = isAdminContext || !!userSession?.account?.isAdmin;
	const { selectedCategory } = useTagsDataContext();

	const [menuOpen, setMenuOpen] = useState<boolean>(false);

	const navigateToTag = () => {
		const filterRequest: IContactsFilterRequest = {
			...DefaultContactsFilterRequest,
			criteria: [
				{
					property: ContactFilterCriteriaProperty.Tag,
					value: tag.name,
				},
			],
		};

		const filter: IBulkContactsRequest = {
			filter: filterRequest,
		};

		const querySort: ISortDescriptor = {
			sort: 'asc',
			sortBy: 'handle',
		};
		const query = searchRequestToQueryStringParams<IBulkContactsRequest>(filter, querySort);

		history.push(`${ROUTES.contacts.people}/?search=${query.search}&sort=${query.sort}`);
	};

	const onContactsTagClicked = () => {
		if (isAdminContext) return;
		navigateToTag();
	};

	const tagColorMutation = useTagColorMutation({
		onSuccess: refreshTags,
		onError: error => {
			toast.error(`An error occured while changing ${tag.name} color.`);
			errors.pushApiError(error);
		},
	});
	const handleColorChange = ({ id, color }: { id: string; color: keyof ThemeColors['pill'] }) => {
		tagColorMutation.mutate({ tagId: id, color, impersonationContext });
	};

	const tagLockedMutation = useTagLockedMutation({
		onSuccess: refreshTags,
		onError: error => {
			toast.error(`An error occurred while changing the lock status of ${tag.name}.`);
			errors.pushApiError(error);
		},
	});
	const handleLockedChange = (isLocked: boolean) => {
		tagLockedMutation.mutate({ tagId: tag.id, isLocked, impersonationContext });
	};

	const handleEditTagAlert = () => {
		onEditTagAlert(tag);
		setMenuOpen(false);
	};

	const handleUpdateCategory = () => {
		onUpdateCategory(tag);
		setMenuOpen(false);
	};

	const putTagCategory = usePutTagCategoryMutation({
		onSuccess: () => {
			toast.success(`${tag.name} removed from category.`);
			if (selectedCategory) {
				invalidateTagCategoryQuery({ categoryId: selectedCategory.id });
			} else {
				refreshTags();
			}
		},
		onError: error => {
			toast.error(`An error occurred while trying to remove ${tag.name} from category.`);
			errors.pushApiError(error);
		},
	});
	const handleRemoveTag = () => {
		putTagCategory.mutate({ categoryId: null, tagId: tag.id, impersonationContext });
	};

	return (
		<DataGridRow>
			<DataGridRowItem>
				<Checkbox checked={isSelected} onCheckedChange={() => onToggleTagRow(tag.id)} size='lg' />
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				{!isAdminContext && (
					<TagFavoriteColumn tag={tag} impersonationContext={impersonationContext} refreshTags={refreshTags} />
				)}
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				<ColorPicker
					color={tag.color}
					onColorChange={color => handleColorChange({ id: tag.id, color })}
					disabled={!!tag.category?.id}
					disabledMessage='Cannot change color of grouped tags. Change group color instead.'
				/>
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				{canManageTagLock && (
					<TagLockToggle
						tag={tag}
						onToggle={handleLockedChange}
						isPending={tagLockedMutation.isPending}
						disabled={!!tag.category?.id}
						disabledMessage='Cannot change the lock status of grouped tags. Change at the group level.'
					/>
				)}
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				<Pill variant='tag' color={tag.color} label={tag.name} onClick={() => onContactsTagClicked()} />
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				<a onClick={() => onContactsTagClicked()} className={css(styleSheet.link)}>
					{tag.stats?.contacts}
				</a>
			</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>{tag.stats?.companies}</DataGridRowItem>
			<DataGridRowItem styles={[styleSheet.cell]}>
				{!isAdminContext ? (
					!tag.tagAlert ? (
						<Button kind='reverse' size='small' onClick={() => onEditTagAlert(tag)}>
							<div className={css(styleSheet.addTagButton)}>
								<PlusIcon fillColor={brandSecondary} />
								<span>Add Tag Notification</span>
							</div>
						</Button>
					) : (
						<TagAlert tag={tag} onEdit={() => onEditTagAlert(tag)} isAdminContext={isAdminContext} />
					)
				) : null}
			</DataGridRowItem>
			<DataGridRowItem>
				<Popover open={menuOpen} onOpenChange={setMenuOpen}>
					<PopoverTrigger
						className={css(styleSheet.menuTrigger)}
						disabled={!!isAdminContext && !tag.tagAlert && !!selectedCategory && !tag?.category?.id}
					>
						<EllipsisIcon />
					</PopoverTrigger>
					<PopoverContent align='end' className={css(styleSheet.menuContent)}>
						<Column>
							{!isAdminContext && !!tag.tagAlert && (
								<ButtonBase className={css(styleSheet.menuItem)} onClick={handleEditTagAlert}>
									Edit Tag Alert
								</ButtonBase>
							)}
							{!selectedCategory && (
								<ButtonBase className={css(styleSheet.menuItem)} onClick={handleUpdateCategory}>
									Add to Category
								</ButtonBase>
							)}
							{tag?.category?.id && (
								<ButtonBase className={css(styleSheet.menuItem)} onClick={handleRemoveTag}>
									Remove from Category
								</ButtonBase>
							)}
						</Column>
					</PopoverContent>
				</Popover>
			</DataGridRowItem>
		</DataGridRow>
	);
};
